import { ErrorCode } from '@ps/web-core-enums';
import { NumericPIN, PIN } from '@ps/web-shared-components';
import { createForm } from '@ps/web-shared-ui';
import type { FormApi } from 'final-form';
import { FORM_ERROR } from 'final-form';
import type { SyntheticEvent } from 'react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { LoginPayload } from '@swf/core/models/auth/login';
import type { Dispatch, RootState } from '@swf/core/store';

import ErrorMessage from '../ErrorMessage/ErrorMessage';
import NextButton from '../NextButton/NextButton';
import PINFormText from '../PINFormText/PINFormText';

import styles from './PinForm.scss';

interface FormValues {
  pin: string;
}

const Form = createForm<FormValues>();

const PINForm: React.FC = () => {
  const {
    auth: { login, pinReset }
  } = useDispatch<Dispatch>();
  const searchParams = useSearchParams<LoginPayload & { errorCode: ErrorCode }>();
  const { push } = useHistory();
  const loading = useSelector((rootState: RootState) => !!rootState.loading?.models.auth);
  const isTempPin =
    searchParams.errorCode === ErrorCode.TEMP_PIN_REQUIRED ||
    searchParams.errorCode === ErrorCode.TEMP_PIN_REQUIRED_SMS;

  const handleSubmit = async ({ pin }: FormValues, formApi: FormApi<FormValues>) => {
    const errorCode = await login({ ...searchParams, pin });
    formApi.change('pin', undefined);

    if (errorCode) {
      return { [FORM_ERROR]: 'ERROR_STATUS_CODE' };
    }

    return {};
  };

  const handleClick = async (e: SyntheticEvent) => {
    e.preventDefault();
    await pinReset({ userId: searchParams.userId });
    push({ pathname: '/auth/login/pin-reset' });
  };

  return (
    <Form
      onSubmit={handleSubmit}
      render={({ pristine }) => (
        <>
          <ErrorMessage />
          <PINFormText errorCode={searchParams.errorCode} handleClick={handleClick}>
            {isTempPin ? (
              <NumericPIN className={styles.numericPin} id="pin" name="pin" label="" />
            ) : (
              <PIN id="pin" name="pin" />
            )}
            <NextButton
              className={styles.next}
              block
              type="submit"
              loading={loading}
              disabled={pristine}
            />
          </PINFormText>
        </>
      )}
    />
  );
};

export default PINForm;
